import { Component, EventEmitter, Inject, Input, LOCALE_ID, OnInit, Output } from '@angular/core';
import { BreakpointObserver } from '@angular/cdk/layout';
import { localizeUserStatus, User, UserStatusType } from '../../model/auth/User';
import { MobileAwareComponent } from '../../common/mobile-aware/mobile-aware.component';
import { LocationService } from '../../service/location.service';
import { Locale } from '../../model/Locale';
import { Contest } from '../../model/contest/Contest';
import { localizePaymentStatus, mobilePaymentStatusIcon, PaymentStatus } from '../../model/Payment';
import { localizeRole, Role } from '../../model/auth/Role';
import { Region } from '../../model/location/Region';
import { School } from '../../model/location/School';
import { SchoolDetailsData, SchoolDetailsModalComponent } from '../school-details-modal/school-details-modal.component';
import {
  ProfileDetailsModalComponent,
  UserDetailsData,
  UserDetailsUpdateResult
} from '../profile-details-modal/profile-details-modal.component';
import { NotificationHandler } from '../../helper/notification.handler';
import { UrlHelper } from '../../helper/url.helper';
import {
  ConfirmationContext,
  ConfirmationData,
  ConfirmationModalComponent
} from '../../common/modal/confirmation-modal/confirmation-modal.component';
import {
  ChangePassResultModalComponent,
  ChangePassResultResult
} from '../change-pass-result-modal/change-pass-result-modal.component';
import {
  ExamProgressModalComponent,
  ProgressDetailsData
} from '../../stats/exam/exam-progress-modal/exam-progress-modal.component';
import { MatDialog } from '@angular/material/dialog';
import { CompetitionService } from '../../service/competition.service';
import { UserService } from '../../service/user.service';
import { ArrayDataSource, SelectionModel } from '@angular/cdk/collections';
import { Observable, of } from 'rxjs';
import { ListItemDetailsData } from '../../common/list-item-details/list-item-details.component';
import { animate, state, style, transition, trigger } from '@angular/animations';
import {
  SelectTeacherModalComponent,
  TeacherSelectData
} from '../../teacher/select-teacher-modal/select-teacher-modal.component';

export interface UserDetails {
  user: User;
  competitionId: string;
}

export interface UserPaymentDetails extends UserDetails {
  contest: Contest;
}

export interface UserRequestDetails extends UserPaymentDetails {
  approve: boolean;
}

export type UserSelectChecker = (user: User) => boolean;

export interface UserSelectionModel {
  title: string;
  type: string;
  cta_title: string;
  checker: UserSelectChecker;
  highlighter: UserSelectChecker;
}

@Component({
  selector: 'users-table',
  templateUrl: './users-table.component.html',
  styleUrls: ['./users-table.component.scss'],
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({height: '0px', minHeight: '0'})),
      state('expanded', style({height: '*'})),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
  ],
})
export class UsersTableComponent extends MobileAwareComponent implements OnInit {

  readonly paymentStatuses = PaymentStatus;
  readonly TEACHER_ROLE = Role.Teacher;

  @Input() roles!: Role[];

  @Input() competitionId!: string;
  @Input() userRole!: Role;

  @Input() set users(users: User[] | undefined) {
    if (users) {
      this.tableDS = new ArrayDataSource<User>(users);
      this.usersSource = users;
    }
  }

  usersSource: User[] = [];
  tableDS = new ArrayDataSource<User>([]);

  @Input() region?: Region;
  @Input() school?: School;

  @Input() showLocation = false;
  @Input() showPhone = false;
  @Input() showPayment = false;
  @Input() showStatus = false;
  @Input() showGrade = false;
  @Input() showSchool = false;
  @Input() showTeacher = false;
  @Input() editAllowed = false;

  @Input() selectionChoices: UserSelectionModel[] = [];
  selectionModel?: UserSelectionModel;

  @Input() deleteAllowed = false;
  @Input() deletePaymentCheck = false;

  @Input() showContact = false;
  @Input() showApprove = false;

  @Input() showUsername = false;

  @Input() showContestProgress = false;

  @Input() showActions = true;

  @Input() contest?: Contest;
  @Input() openForEnrol: boolean = false;

  @Output() deleteRequested = new EventEmitter<UserDetails>();
  @Output() handlePaymentRetry = new EventEmitter<UserPaymentDetails>();
  @Output() handlePaymentVerify = new EventEmitter<UserPaymentDetails>();
  @Output() usersSelectedCta = new EventEmitter<{ users: User[]; model: UserSelectionModel }>();
  @Output() userUpdated = new EventEmitter<User>();
  @Output() userRequestUpdated = new EventEmitter<UserRequestDetails>();

  regions: Map<string, string> = new Map<string, string>();

  userDetails: ListItemDetailsData<UserDetails> | null = null;

  columns: string[] = [];
  mobileColumns: string[] = [];
  selection = new SelectionModel<User>(true, [], false);

  constructor(private locationService: LocationService,
              private competitionService: CompetitionService,
              private userService: UserService,
              private dialog: MatDialog,
              @Inject(LOCALE_ID) private locale: string,
              breakpointObserver: BreakpointObserver) {
    super(breakpointObserver);
  }

  ngOnInit(): void {
    super.ngOnInit();

    this.initColumns();

    if (!this.regions || !this.regions.size) {
      this.loadRegions();
    }
  }

  private initColumns() {
    if (this.selectionChoices?.length) {
      this.columns.push('select');
      this.mobileColumns.push('select');
    }

    this.columns.push('identifier');
    this.mobileColumns.push('identifier');

    if (this.showPhone) this.columns.push('phone');

    this.columns.push('name');
    this.mobileColumns.push('name');

    if (this.showGrade) this.columns.push('grade');
    if (this.showLocation) this.columns.push('region');
    if (this.showLocation && this.showSchool) this.columns.push('school');

    if (this.showTeacher) this.columns.push('teacher');

    if (this.usersSource.find(user => !!user.enrolledAt)) this.columns.push('enrolled');

    if (this.showContact) {
      this.columns.push('contact');
      this.mobileColumns.push('contact');
    }

    if (this.showStatus) {
      this.columns.push('status');
      this.mobileColumns.push('status');
    }
    if (this.showPayment) {
      this.columns.push('payment');
      this.mobileColumns.push('payment');
    }

    if(this.showActions) {
      this.columns.push('actions');
      this.mobileColumns.push('actions');
    }
  }

  public updateData(): void {
    this.tableDS = new ArrayDataSource<User>(this.usersSource || []);
    this.userDetails = null;
  }

  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.usersSource.filter(user => this.selectionModel?.checker(user)).length || 0;
    return numSelected === numRows;
  }

  selectAllToggle() {
    if (this.isAllSelected()) {
      this.selection.clear();
      return;
    }

    this.selection.select(...(this.usersSource.filter(user => this.selectionModel?.checker(user)) || []));
  }

  compareSelections(s1?: UserSelectionModel, s2?: UserSelectionModel): boolean {
    return s1 != undefined && s2 != undefined && s1.type === s2.type;
  }

  displaySelection(selectionModel: UserSelectionModel): string {
    return selectionModel.title;
  }

  loadSelections = (): Observable<UserSelectionModel[]> => {
    return of([{
      title: $localize`:Users List|No operation title@@users-list-no-operation-title:No Operation`,
      type: 'no-selection',
      cta_title: '',
      checker: () => false,
      highlighter: () => false,
    }, ...this.selectionChoices]);
  };

  private loadRegions(): void {
    this.locationService.getRegions(this.locale as Locale).subscribe(regions => {
      regions.forEach(region => this.regions.set(region.id, region.name));
    });
  }

  localizeStatus(paymentStatus: PaymentStatus) {
    return localizePaymentStatus(paymentStatus);
  }

  localizeUserStatus(status: UserStatusType) {
    return localizeUserStatus(status);
  }

  localizeRole(role: Role) {
    return localizeRole(role);
  }

  mobileStatusIcon(paymentStatus: PaymentStatus) {
    return mobilePaymentStatusIcon(paymentStatus);
  }

  isWatchingResultsAllowed(user: User): boolean {
    return this.userRole == Role.Student
      && user.paymentStatus == PaymentStatus.successful
      && !!this.contest
      && !!this.contest.endsAt
      && this.contest.endsAt < new Date();
  }

  isWatchingProgressAllowed(user: User): boolean {
    return this.showContestProgress
      && this.userRole == Role.Student
      && user.paymentStatus == PaymentStatus.successful
      && !!this.contest
      && !!this.contest.startsAt
      && this.contest.startsAt < new Date();
  }

  setPasswordAllowed(user: User): boolean {
    return this.editAllowed && this.showUsername && !user.email;
  }

  isChangingTeacherAllowed(): boolean {
    return this.showTeacher
      && this.userRole == Role.Student
      && !!this.contest?.endsAt
      && this.contest.endsAt > new Date();
  }

  getUserDetails(user: User): ListItemDetailsData<UserDetails> {
    const data: ListItemDetailsData<UserDetails> = {
      details: [],
      actions: [],
      component: {
        user: user,
        competitionId: this.competitionId
      }
    };

    if (this.showUsername) {
      data.details.push({
        name: $localize`:Users List|Users List item 'identifier' title@@users-list-item-identifier-title:Identifier:`,
        value: user.username || user.email
      });
    } else {
      data.details.push({
        name: $localize`:Users List|Users List item 'email' title@@users-list-item-email-title:Email:`,
        value: user.email
      });
    }

    if (this.showPhone) {
      data.details.push({
        name: $localize`:Users List|Users List item 'phone' title@@users-list-item-phone-title:Phone:`,
        value: user.phone || '–'
      });
    }

    data.details.push({
      name: $localize`:Users List|Users List item 'name' title@@users-list-item-name-title:Name:`,
      value: user.firstName || user.lastName ? `${user.firstName} ${user.lastName}` : '–',
    });

    if (user.schoolGrade) {
      data.details.push({
        name: $localize`:Users List|Users List item 'grade' title@@users-list-item-grade-title:Grade:`,
        value: user.schoolGrade
      });
    }

    if (user.region && this.regions.has(user.region)) {
      data.details.push({
        name: $localize`:Users List|Users List item 'region' title@@users-list-item-region-title:Region:`,
        value: this.regions.get(user.region)
      });
    }
    if (user.schoolName) {
      data.details.push({
        name: $localize`:Users List|Users List item 'school' title@@users-list-item-school-title:School:`,
        value: user.schoolName
      });
    }

    if (this.showTeacher && user.teacher_id) {
      data.details.push({
        name: `${this.localizeRole(Role.Teacher)}:`,
        value: `${user.teacher_first_name} ${user.teacher_last_name}`
      });
    }

    if (user.enrolledAt) {
      data.details.push({
        name: $localize`:Users List|Users List item 'enrolled-at' title@@users-list-item-enrolled-at-title:Enrolled At:`,
        value: user.enrolledAt
      });
    }

    if (this.showStatus) {
      data.details.push({
        name: $localize`:Contests List|Contests list item 'user-status' title@@contests-list-item-user-status-title:Status:`,
        value: this.localizeUserStatus(user.status || 'UNKNOWN'),
      });
    }

    if (this.showPayment) {
      data.details.push({
        name: $localize`:Contests List|Contests list item 'payment' title@@contests-list-item-payment-title:Payment:`,
        value: user.paymentStatus ? this.localizeStatus(user.paymentStatus) : '–',
      });
    }

    return data;
  }

  seeSchoolDetails(schoolId: string) {
    const data: SchoolDetailsData = {
      schoolId: schoolId
    };
    this.dialog.open(SchoolDetailsModalComponent, {
      width: '600px',
      data: data
    });
  }

  removeUser(user: User) {
    this.deleteRequested.emit({
      user: user,
      competitionId: this.competitionId
    });
  }

  retryClicked(user: User, contest: Contest) {
    this.handlePaymentRetry.emit({
      user: user,
      contest: contest,
      competitionId: this.competitionId
    });
  }

  verifyClicked(user: User, contest: Contest) {
    this.handlePaymentVerify.emit({
      user: user,
      contest: contest,
      competitionId: this.competitionId
    });
  }

  editUserDetails(user: User) {
    const data: UserDetailsData = {
      userId: user.id,
      userRole: this.userRole,

      userSchoolId: user.schoolId,
      userSchoolName: user.schoolName,

      competitionId: this.competitionId,
      contest: this.contest,

      mobileView: this.mobileView
    };
    this.dialog.open(ProfileDetailsModalComponent, {
      width: '600px',
      data: data
    }).afterClosed().subscribe((data: UserDetailsUpdateResult) => {
      if (data && data.user) {
        this.handleUserUpdated(data.user);
      }
    });
  }

  updateContact(user: User, checked: boolean) {
    if (this.userRole == Role.Host) {
      this.competitionService.updateContactHost(this.competitionId, user.id, checked).subscribe({
        next: host => {
          this.handleUserUpdated(host);
        },
        error: error => {
          NotificationHandler.handleError($localize`:Notifications|Update competition contact host has failed message@@notifications-update-competition-contact-host-has-failed-error:Update competition contact host has failed`, error);
        }
      });
    }
  }

  handleUserUpdated(user: User) {
    if (this.usersSource) {
      const index = this.usersSource.findIndex(u => u.id == user.id);
      if (index >= 0) {
        const updatedUser = this.usersSource[index];

        updatedUser.email = user.email;
        updatedUser.phone = user.phone;

        updatedUser.firstName = user.firstName;
        updatedUser.lastName = user.lastName;
        updatedUser.schoolGrade = user.schoolGrade;

        if (this.showTeacher && (updatedUser.region != user.region || updatedUser.schoolId != user.schoolId)) {
          updatedUser.teacher_id = undefined;
          updatedUser.teacher_first_name = undefined;
          updatedUser.teacher_last_name = undefined;
        }

        if ((this.region && user.region && this.region.id != user.region)) {
          this.usersSource.splice(index, 1);
          if(this.userDetails && this.userDetails.component?.user.id == user.id) this.userDetails = null;
          this.tableDS = new ArrayDataSource<User>(this.usersSource);
        } else {
          updatedUser.region = user.region;

          if ((this.school && user.schoolId && this.school.id != user.schoolId)) {
            this.usersSource.splice(index, 1);
            if(this.userDetails && this.userDetails.component?.user.id == user.id) this.userDetails = null;
            this.tableDS = new ArrayDataSource<User>(this.usersSource);
          } else {
            updatedUser.schoolId = user.schoolId;
            updatedUser.schoolName = user.schoolName;
          }
        }

        if (user.contactPerson != undefined && updatedUser.contactPerson) {
          user.contactPerson = updatedUser.contactPerson;
        }
      }
    }

    this.userUpdated.emit(user);
  }

  handleRequestAction(user: User, approve: boolean) {
    if (!this.contest) return;

    this.userRequestUpdated.emit({
      approve: approve,
      user: user,
      contest: this.contest,
      competitionId: this.competitionId
    });
  }

  statusTooltip(status: UserStatusType, emailVerified: boolean, paymentStatus?: PaymentStatus) {
    const statusDesc = `${$localize`:Contests List|Contests list item user status tooltip desc@@contests-list-item-user-status-tooltip-desc:User Status:`} ${this.localizeUserStatus(status)}`;
    const actions = status == 'CONFIRMED'
      ? emailVerified
        ? paymentStatus == PaymentStatus.successful
          ? $localize`:Contests List|Contests list item user confirmed and verified tooltip action@@contests-list-item-user-confirmed-and-verified-tooltip-desc:A profile is active and verified.\r\nA participant will be able to attend the contest and receive notifications.`
          : $localize`:Contests List|Contests list item user confirmed and verified but not paid tooltip action@@contests-list-item-user-confirmed-and-verified-not-paid-tooltip-desc:A profile is active and verified.\r\nA participant can receive notifications and will be able to attend the contest after successful payment.`
        : paymentStatus == PaymentStatus.successful
          ? $localize`:Contests List|Contests list item user confirmed but not verified tooltip action@@contests-list-item-user-confirmed-but-not-verified-tooltip-desc:The invitation is accepted, but email is not confirmed.\r\nUser will be able to attend the contest but would not receive any notifications.`
          : $localize`:Contests List|Contests list item user confirmed but not verified and not paid tooltip action@@contests-list-item-user-confirmed-but-not-verified-not-paid-tooltip-desc:The invitation is accepted, but email is not confirmed.\r\nA participant cannot receive notifications but will be able to attend the contest after successful payment.`
      : status == 'FORCE_CHANGE_PASSWORD'
        ? $localize`:Contests List|Contests list item user not confirmed tooltip action@@contests-list-item-user-not-confirmed-tooltip-desc:The invitation is not accepted yet.\r\nA participant will not be able to attend the contest until his profile is activated.`
        : '';
    return `${statusDesc}\r\n${actions}`;
  }

  getExamCardUrl(user: User) {
    return `${UrlHelper.STATS_EXAM_CARD_PATH}${this.contest?.id}/${user.id}`;
  }

  resetPassword(user: User) {
    if (!this.showUsername || !user.username) return;

    const data: ConfirmationData = {
      title: $localize`:Students List|Get Passwords button title@@students-list-get-passwords-button-title:Get Passwords`,
      content: $localize`:Students List|Get Passwords details@@students-list-get-passwords-details:If you proceed, all users you selected will need to use newly generated passwords. Do you want to continue?`
    };
    const dialogRef = this.dialog.open(ConfirmationModalComponent, {
      width: '600px',
      data: data
    });

    dialogRef.componentInstance.actionConfirmed.subscribe((context: ConfirmationContext) => {
      context.modal.startProgress();

      this.userService.changeUserPasswords([user.username as string], false).subscribe({
        next: results => {
          const changePassResults = new Map<string, string>();
          results.forEach(result => changePassResults.set(result.username, result.password));

          context.modal.proceedHandled();

          const data: ChangePassResultResult = {
            users: [user],
            changePassResults
          };
          this.dialog.open(ChangePassResultModalComponent, {
            width: '600px',
            data: data
          });
        },
        error: error => {
          NotificationHandler.handleError($localize`:Notifications|Change students passwords has failed message@@notifications-change-students-passwords-failed-error:Change students passwords has failed`, error);
        }
      });
    });
  }

  showParticipationStatus(user: User) {
    if (!this.contest || !this.competitionId) return;

    const data: ProgressDetailsData = {
      userId: user.id,
      firstName: user.firstName,
      lastName: user.lastName,

      competitionId: this.competitionId,
      contest: this.contest,
    };
    this.dialog.open(ExamProgressModalComponent, {
      width: '600px',
      data: data
    });
  }

  selectTeacher(user: User) {
    if (!this.contest || !user.region || !user.schoolId) return;

    const data: TeacherSelectData = {
      competitionId: this.competitionId,
      contestId: this.contest.id,
      regionId: user.region,
      schoolId: user.schoolId,
      userIds: [user.id],

      ...(user.teacher_id && {
        teacher: {
          teacher_id: user.teacher_id,
          teacher_first_name: user.teacher_first_name,
          teacher_last_name: user.teacher_last_name,
        }
      })
    };

    this.dialog.open(SelectTeacherModalComponent, {
      width: '600px',
      data: data
    }).afterClosed().subscribe((users: User[]) => {
      if (users?.length) {
        const u = users[0];
        user.teacher_id = u.teacher_id;
        user.teacher_first_name = u.teacher_first_name;
        user.teacher_last_name = u.teacher_last_name;
      }
    });
  }

  selectionModelChanged(model: UserSelectionModel) {
    this.selection.clear();
    this.userDetails = null;
    this.selectionModel = model;
  }
}
